// src/pages/modules/DataEntry/RegisterForm/FormFieldRenderer.tsx
import React from 'react'
import { Badge, Col, Form, InputGroup } from 'react-bootstrap'
import type { Field } from '../../../../_services/formEngine'

interface Props {
  field: Field
  value: any
  onChange: (f: Field, val: any) => void
  onToggleMulti: (f: Field, v: string) => void
  readOnly?: boolean
  showScore?: boolean
  scoreValue?: number
  onScoreChange?: (f: Field, score: number) => void
}

export default function FormFieldRenderer({
  field: f,
  value,
  onChange,
  onToggleMulti,
  readOnly = false,
  showScore = false,
  scoreValue,
  onScoreChange,
}: Props) {
  const inputProps = { disabled: readOnly, size: 'sm' as const }

  const renderInput = () => {
    switch (f.type) {
      case 'text':
      case 'url':
      case 'phone':
        return (
          <Form.Control
            {...inputProps}
            type='text'
            value={value ?? ''}
            placeholder={f.placeholder}
            onChange={(e) => onChange(f, e.target.value)}
          />
        )

      case 'number':
        return (
          <Form.Control
            {...inputProps}
            type='number'
            value={value ?? ''}
            placeholder={f.placeholder}
            onChange={(e) => onChange(f, e.target.value === '' ? '' : Number(e.target.value))}
          />
        )

      case 'date':
        return (
          <Form.Control
            {...inputProps}
            type='date'
            value={value ?? ''}
            onChange={(e) => onChange(f, e.target.value)}
          />
        )

      case 'textarea':
        return (
          <Form.Control
            {...inputProps}
            as='textarea'
            rows={2}
            value={value ?? ''}
            placeholder={f.placeholder}
            onChange={(e) => onChange(f, e.target.value)}
          />
        )

      case 'radio':
        return (
          <div className='d-flex gap-3 flex-wrap pt-1'>
            {(f.options || []).map((opt) => (
              <Form.Check
                key={opt.value}
                type='radio'
                id={`${f.name}_${opt.value}`}
                label={opt.label}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(f, opt.value)}
                disabled={readOnly}
                inline
              />
            ))}
          </div>
        )

      case 'select':
        if (f.multiple) {
          return (
            <div className='d-flex gap-2 flex-wrap pt-1'>
              {(f.options || []).map((opt) => {
                const arr: string[] = Array.isArray(value) ? value : []
                return (
                  <Form.Check
                    key={opt.value}
                    type='checkbox'
                    id={`${f.name}_${opt.value}`}
                    label={opt.label}
                    checked={arr.includes(opt.value)}
                    onChange={() => onToggleMulti(f, opt.value)}
                    disabled={readOnly}
                    inline
                  />
                )
              })}
            </div>
          )
        }
        return (
          <Form.Select
            {...inputProps}
            value={value ?? ''}
            onChange={(e) => onChange(f, e.target.value)}
          >
            <option value=''>-- เลือก --</option>
            {(f.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        )

      case 'checkbox':
        if (f.multiple) {
          return (
            <div className='d-flex gap-2 flex-wrap pt-1'>
              {(f.options || []).map((opt) => {
                const arr: string[] = Array.isArray(value) ? value : []
                return (
                  <Form.Check
                    key={opt.value}
                    type='checkbox'
                    id={`${f.name}_${opt.value}`}
                    label={opt.label}
                    checked={arr.includes(opt.value)}
                    onChange={() => onToggleMulti(f, opt.value)}
                    disabled={readOnly}
                    inline
                  />
                )
              })}
            </div>
          )
        }
        return (
          <Form.Check
            type='checkbox'
            id={f.name}
            label={f.label}
            checked={!!value}
            onChange={(e) => onChange(f, e.target.checked)}
            disabled={readOnly}
          />
        )

      default:
        return <Form.Control {...inputProps} value={value ?? ''} onChange={(e) => onChange(f, e.target.value)} />
    }
  }

  return (
    <Col xs={12} md={6}>
      <Form.Group>
        <Form.Label className='mb-1' style={{ fontSize: 13, fontWeight: 600 }}>
          {f.label}
          {f.required && <span className='text-danger ms-1'>*</span>}
        </Form.Label>

        {/* ถ้า scorable = true ให้แสดง input คะแนนข้างหลัง */}
        {showScore && f.scorable && onScoreChange ? (
          <InputGroup size='sm'>
            <div style={{ flex: 1 }}>{renderInput()}</div>
            <InputGroup.Text style={{ fontSize: 11, padding: '0 6px', backgroundColor: '#fff8e1' }}>
              คะแนน
            </InputGroup.Text>
            <Form.Control
              type='number'
              size='sm'
              min={0}
              value={scoreValue ?? 0}
              onChange={(e) => onScoreChange(f, Number(e.target.value) || 0)}
              disabled={readOnly}
              style={{ maxWidth: 64, textAlign: 'center' }}
            />
          </InputGroup>
        ) : (
          renderInput()
        )}
      </Form.Group>
    </Col>
  )
}

// ─── ScoreBar ─────────────────────────────────────────────────
export function ScoreBar({ totalScore }: { totalScore: number }) {
  return (
    <Badge bg='success' style={{ fontSize: 11 }}>
      รวมคะแนน {totalScore}
    </Badge>
  )
}